// Admin Complaints queue — KPIs strip + filter bar + table.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle, Inbox, TrendingUp, Clock, ShieldAlert, Bot, Users, Search,
} from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const STATUS_OPTIONS = ["", "open", "awaiting_customer", "in_progress", "resolved", "closed"];
const SEVERITY_OPTIONS = ["", "low", "medium", "high", "critical"];
const SUBTYPE_OPTIONS = [
    "", "service_quality", "product_defect", "delayed_delivery", "wrong_item",
    "billing_dispute", "refund_delay", "doctor_consult", "lab_test",
    "privacy", "safety", "other",
];
const SENTIMENT_OPTIONS = ["", "very_negative", "negative", "neutral", "positive"];

const SEVERITY_TONE = {
    low: "border-slate-200 bg-slate-50 text-slate-700",
    medium: "border-amber-200 bg-amber-50 text-amber-800",
    high: "border-orange-200 bg-orange-50 text-orange-800",
    critical: "border-rose-300 bg-rose-50 text-rose-800",
};

const STATUS_TONE = {
    open: "bg-emerald-100 text-emerald-700",
    awaiting_customer: "bg-amber-100 text-amber-800",
    in_progress: "bg-blue-100 text-blue-700",
    awaiting_agent: "bg-blue-100 text-blue-700",
    resolved: "bg-zinc-100 text-zinc-700",
    closed: "bg-zinc-200 text-zinc-600",
};

const Kpi = ({ icon: Icon, label, value, tone = "brand", testId }) => {
    const tones = {
        brand: "bg-brand-50 text-brand-700",
        rose: "bg-rose-50 text-rose-700",
        emerald: "bg-emerald-50 text-emerald-700",
        amber: "bg-amber-50 text-amber-700",
    };
    return (
        <div className="rounded-2xl border border-zinc-100 bg-white p-4 shadow-sm" data-testid={testId}>
            <div className="flex items-center gap-3">
                <div className={`grid h-9 w-9 place-items-center rounded-lg ${tones[tone] || tones.brand}`}>
                    <Icon className="h-4 w-4" />
                </div>
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
                    <p className="text-xl font-semibold text-zinc-900">{value ?? "—"}</p>
                </div>
            </div>
        </div>
    );
};

const AdminComplaintsContent = () => {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        status: "",
        severity: "",
        subtype: "",
        sentiment: "",
        assigned: "",
        breached_only: false,
        q: "",
    });
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const pageSize = 30;

    const load = async () => {
        setLoading(true);
        try {
            const params = {
                status: filters.status || undefined,
                severity: filters.severity || undefined,
                subtype: filters.subtype || undefined,
                sentiment: filters.sentiment || undefined,
                assigned: filters.assigned || undefined,
                breached_only: filters.breached_only || undefined,
                page,
                page_size: pageSize,
            };
            const [{ data: list }, { data: kpi }] = await Promise.all([
                api.get("/admin/complaints", { params }),
                api.get("/admin/complaints-stats"),
            ]);
            // Client-side simple subject filter
            const q = filters.q.trim().toLowerCase();
            const visible = !q ? list.items : list.items.filter((r) =>
                (r.subject || "").toLowerCase().includes(q)
                || (r.ticket_no || "").toLowerCase().includes(q)
                || (r.user_email || "").toLowerCase().includes(q)
            );
            setItems(visible);
            setTotal(list.total);
            setStats(kpi);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.severity, filters.subtype, filters.sentiment, filters.assigned, filters.breached_only, page]);
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filters.q]);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

    return (
        <div className="space-y-5" data-testid="admin-complaints-page">
            {/* KPIs */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <Kpi icon={Inbox} label="Open" value={stats?.open ?? "—"} tone="brand" testId="kpi-open" />
                <Kpi icon={ShieldAlert} label="Breached" value={stats?.breached ?? "—"} tone="rose" testId="kpi-breached" />
                <Kpi icon={Bot} label="Deflection rate" value={`${stats?.self_help_deflection_rate_pct ?? 0}%`} tone="emerald" testId="kpi-deflection" />
                <Kpi icon={Clock} label="Avg resolution" value={stats?.avg_resolution_minutes ? `${Math.round(stats.avg_resolution_minutes)} min` : "—"} tone="amber" testId="kpi-resolution" />
                <Kpi icon={TrendingUp} label="Resolved" value={stats?.resolved_total ?? "—"} tone="brand" testId="kpi-resolved" />
            </div>

            {/* Filter bar */}
            <div className="rounded-2xl border border-zinc-100 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-end gap-3" data-testid="admin-complaints-filters">
                    <div className="relative min-w-[220px] flex-1">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
                        <input
                            value={filters.q}
                            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                            placeholder="Search subject, ticket #, or email…"
                            className="w-full rounded-lg border border-zinc-200 pl-8 pr-3 py-2 text-sm outline-none focus:border-brand-500"
                            data-testid="admin-complaints-search"
                        />
                    </div>
                    <Select label="Status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={STATUS_OPTIONS} testId="filter-status" />
                    <Select label="Severity" value={filters.severity} onChange={(v) => setFilters((f) => ({ ...f, severity: v }))} options={SEVERITY_OPTIONS} testId="filter-severity" />
                    <Select label="Subtype" value={filters.subtype} onChange={(v) => setFilters((f) => ({ ...f, subtype: v }))} options={SUBTYPE_OPTIONS} testId="filter-subtype" />
                    <Select label="Sentiment" value={filters.sentiment} onChange={(v) => setFilters((f) => ({ ...f, sentiment: v }))} options={SENTIMENT_OPTIONS} testId="filter-sentiment" />
                    <Select label="Assigned" value={filters.assigned} onChange={(v) => setFilters((f) => ({ ...f, assigned: v }))} options={["", "me", "unassigned"]} testId="filter-assigned" />
                    <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-zinc-700">
                        <input
                            type="checkbox"
                            checked={filters.breached_only}
                            onChange={(e) => setFilters((f) => ({ ...f, breached_only: e.target.checked }))}
                            className="h-3.5 w-3.5 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                            data-testid="filter-breached"
                        />
                        SLA breached only
                    </label>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-zinc-100" data-testid="admin-complaints-table">
                    <thead className="bg-zinc-50/60 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                        <tr>
                            <th className="px-4 py-3">Ticket #</th>
                            <th className="px-4 py-3">Subject</th>
                            <th className="px-4 py-3">Customer</th>
                            <th className="px-4 py-3">Severity</th>
                            <th className="px-4 py-3">Subtype</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Updated</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 text-sm">
                        {loading ? (
                            [1, 2, 3].map((i) => (
                                <tr key={i}>
                                    <td colSpan={7} className="px-4 py-3"><div className="h-6 skeleton rounded-lg" /></td>
                                </tr>
                            ))
                        ) : items.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-4 py-12 text-center text-zinc-500">
                                    No complaints match these filters.
                                </td>
                            </tr>
                        ) : (
                            items.map((c) => {
                                const sev = c.severity_admin || c.severity_auto || "medium";
                                return (
                                    <tr key={c.id} className="hover:bg-zinc-50/50" data-testid={`row-complaint-${c.id}`}>
                                        <td className="px-4 py-3">
                                            <Link to={`/admin/complaints/${c.id}`} className="font-mono text-xs text-brand-700 hover:underline">
                                                {c.ticket_no}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-3 max-w-xs">
                                            <Link to={`/admin/complaints/${c.id}`} className="line-clamp-1 font-medium text-zinc-900 hover:text-brand-700">
                                                {c.subject}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="text-zinc-900">{c.user_name || c.user_email}</p>
                                            {c.customer_pills_id && <p className="text-[10px] text-zinc-400 font-mono">{c.customer_pills_id}</p>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${SEVERITY_TONE[sev]}`}>{sev}</span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-zinc-600">{(c.complaint_subtype || "—").replace(/_/g, " ")}</td>
                                        <td className="px-4 py-3">
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_TONE[c.status] || "bg-zinc-100 text-zinc-700"}`}>
                                                {(c.status || "").replace("_", " ")}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-[11px] text-zinc-500">{new Date(c.updated_at).toLocaleString()}</td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
                {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-zinc-100 bg-zinc-50/60 px-4 py-2 text-xs text-zinc-600">
                        <span>Page {page} of {totalPages} · {total} total</span>
                        <div className="flex gap-2">
                            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-md border border-zinc-200 px-2 py-1 disabled:opacity-50">Prev</button>
                            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-md border border-zinc-200 px-2 py-1 disabled:opacity-50">Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const Select = ({ label, value, onChange, options, testId }) => (
    <label className="flex flex-col gap-1 text-xs">
        <span className="font-semibold uppercase tracking-wider text-zinc-500">{label}</span>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
            data-testid={testId}
        >
            {options.map((o) => (
                <option key={o || "all"} value={o}>{o || "All"}</option>
            ))}
        </select>
    </label>
);

const AdminComplaints = () => (
    <AdminAuthGuard>
        <AdminLayout title="Complaints" subtitle="AI-triaged customer complaints with one-click compensation">
            <AdminComplaintsContent />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminComplaints;
